import { LOGO_LOADER_DELAY_MS } from './logo-loader.motion';

/**
 * One wait often passes through several loaders in a row — the boot loader in
 * index.html, the app-shell Suspense fallback, the auth hold, the sync boot.
 * Each is a separate mount, and each would otherwise sit invisible for its own
 * 300 ms grace before fading in: the mark would blink out between them.
 *
 * So the grace belongs to the WAIT, not to the mount: a loader that mounts
 * while another one is on screen, or right after one left, appears at once.
 * Together with the shared phase (`logoLoaderPhaseMs`) the hand-over is
 * invisible — the cards keep shuffling as if it were one loader.
 */

/** How soon after a visible loader leaves the next one still counts as the same wait. */
const HANDOFF_WINDOW_MS = 250;

let visibleLoaders = 0;
let lastVisibleEndAt = Number.NEGATIVE_INFINITY;

export const loaderNow = (): number =>
  typeof performance !== 'undefined' ? performance.now() : Date.now();

/** Should a loader mounting now skip its grace period? */
export function logoLoaderContinuesWait(now = loaderNow()): boolean {
  return visibleLoaders > 0 || now - lastVisibleEndAt < HANDOFF_WINDOW_MS;
}

/**
 * Track one mounted loader. Returns the cleanup to call on unmount. `visibleNow`
 * is true when it appeared without a grace period.
 */
export function trackLogoLoader(visibleNow: boolean): () => void {
  let visible = false;
  const show = () => {
    visible = true;
    visibleLoaders += 1;
  };
  let timer: ReturnType<typeof setTimeout> | undefined;
  if (visibleNow) show();
  else timer = setTimeout(show, LOGO_LOADER_DELAY_MS);
  return () => {
    if (timer) clearTimeout(timer);
    if (visible) {
      visibleLoaders -= 1;
      lastVisibleEndAt = loaderNow();
    }
  };
}

/** A loader drawn outside React (the index.html boot loader) just left the screen. */
export function markLogoLoaderHandoff(now = loaderNow()): void {
  lastVisibleEndAt = now;
}

/**
 * Where in its loop a loader should start so that every loader on the page is
 * in step with one clock (page time). Web feeds it to a negative
 * `animation-delay`; native starts its clock there.
 */
export function logoLoaderPhaseMs(durationMs: number, now = loaderNow()): number {
  return Math.round(now % durationMs);
}
